(() => {
  'use strict';

  const VERSION = 'homepage-top-option-autocommit-v2-empty-skip';
  const ROOT_SELECTOR = '#cap-home-fitment-search';
  const FIELD_SELECTOR = '.cap-home-fitment__field[data-combo]';
  const INPUT_SELECTOR = `${FIELD_SELECTOR} input`;
  const OPTION_SELECTOR = '.cap-home-fitment__menu .cap-home-fitment__option';
  const committingFields = new WeakSet();

  const clean = (value) => String(value || '').replace(/\s+/g, ' ').trim();

  function inputFor(field) {
    return field?.querySelector('input') || null;
  }

  function visibleFields(root) {
    return Array.from(root.querySelectorAll(FIELD_SELECTOR)).filter((field) => {
      return field.dataset.combo !== 'part' || root.dataset.mode === 'parts';
    });
  }

  function firstOption(field) {
    const input = inputFor(field);
    if (
      !field
      || !input
      || input.disabled
      || !field.classList.contains('is-open')
      || !clean(input.value)
    ) {
      return null;
    }

    return Array.from(field.querySelectorAll(OPTION_SELECTOR))
      .find((option) => !option.disabled) || null;
  }

  function nextTarget(root, field) {
    const fields = visibleFields(root);
    const index = fields.indexOf(field);
    if (index >= 0 && fields[index + 1]) return inputFor(fields[index + 1]);
    return root.querySelector('.cap-home-fitment__submit');
  }

  function focusNow(target) {
    if (!target || ('disabled' in target && target.disabled)) return false;

    try {
      target.focus({ preventScroll: true });
    } catch (error) {
      target.focus();
    }

    return document.activeElement === target;
  }

  function waitForReady(root, target, timeoutMs = 7000) {
    return new Promise((resolve) => {
      const status = root.querySelector('.cap-home-fitment__status');
      const startedAt = window.performance.now();

      const check = () => {
        const loading = /loading/i.test(status?.textContent || '');
        const enabled = !target || !('disabled' in target) || !target.disabled;
        const timedOut = window.performance.now() - startedAt >= timeoutMs;

        if ((enabled && !loading) || timedOut) {
          resolve();
          return;
        }

        window.setTimeout(check, 50);
      };

      window.setTimeout(check, 0);
    });
  }

  async function finishTarget(root, target) {
    if (!target) return;

    await waitForReady(root, target);
    if ('disabled' in target && target.disabled) return;

    focusNow(target);
    if (target.matches?.(INPUT_SELECTOR) && document.activeElement === target) {
      target.dispatchEvent(new FocusEvent('focus', { bubbles: false }));
    }
  }

  function commit(root, field, option) {
    if (!field || !option || committingFields.has(field)) return false;

    committingFields.add(field);
    const target = nextTarget(root, field);

    option.dispatchEvent(new MouseEvent('mousedown', {
      bubbles: true,
      cancelable: true,
      view: window,
      button: 0
    }));

    void finishTarget(root, target)
      .finally(() => committingFields.delete(field));

    return true;
  }

  function initialize() {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return false;
    if (root.dataset.homeTopOptionAutocommit === VERSION) return true;

    root.dataset.homeTopOptionAutocommit = VERSION;

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || event.shiftKey) return;

      const input = event.target instanceof Element ? event.target.closest(INPUT_SELECTOR) : null;
      if (!input || !root.contains(input)) return;

      const field = input.closest(FIELD_SELECTOR);
      const option = firstOption(field);

      if (!option) {
        if (root.dataset.salesRep !== 'true' || clean(input.value)) return;
        const target = nextTarget(root, field);
        if (!target || ('disabled' in target && target.disabled)) return;
        if (event.cancelable) event.preventDefault();
        event.stopImmediatePropagation();
        field.classList.remove('is-open');
        focusNow(target);
        return;
      }

      if (event.cancelable) event.preventDefault();
      event.stopImmediatePropagation();
      commit(root, field, option);
    }, true);

    return true;
  }

  function initializeWhenReady() {
    if (initialize()) return;

    let attempts = 0;
    const retry = () => {
      attempts += 1;
      if (initialize() || attempts >= 240) return;
      window.requestAnimationFrame(retry);
    };
    window.requestAnimationFrame(retry);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeWhenReady, { once: true });
  } else {
    initializeWhenReady();
  }
})();
